<template>
  <ACard id="withdrawal-info" :flat="flat">
    <template #title>
      <span class="wd-info__title">
        <i class="material-icons-round wd-info__title-icon">person_off</i>
        탈퇴 정보
        <span v-if="showUser && record" class="wd-info__title-user">사용자 #{{ record.userIdx }}</span>
      </span>
    </template>
    <template #sub>
      탈퇴 시점 기준 스냅샷입니다. 이후 결제·환불이 있어도 이 값은 바뀌지 않습니다.
    </template>
    <template v-if="showUser && record" #action>
      <ABtn size="sm" icon="visibility" @click="$emit('view-user', record.userIdx)">상세 보기</ABtn>
    </template>

    <div v-if="loading" class="wd-info__loading">
      <div class="p-spinner"></div>
      <span>탈퇴 기록을 불러오는 중...</span>
    </div>

    <AEmpty
      v-else-if="!record"
      size="sm"
      title="탈퇴 기록이 없어요"
      sub="탈퇴 기록 도입 이전에 탈퇴한 계정이거나, 기록이 남지 않은 계정입니다."
    />

    <div v-else class="wd-info">
      <dl class="wd-info__grid">
        <div class="wd-info__row">
          <dt>탈퇴 일시</dt>
          <dd>{{ formatDateTime(record.withdrawnAt) }}</dd>
        </div>
        <div class="wd-info__row">
          <dt>처리 구분</dt>
          <dd><APill :kind="sourcePillKind" dot>{{ sourceText }}</APill></dd>
        </div>
        <div class="wd-info__row">
          <dt>재인증 방식</dt>
          <dd>{{ reauthText }}</dd>
        </div>
        <div class="wd-info__row">
          <dt>이용권 소멸 동의</dt>
          <dd>
            <template v-if="planCount > 0">
              <APill :kind="record.planForfeitAgreed ? 'ok' : 'err'" dot>
                {{ record.planForfeitAgreed ? '동의' : '미동의' }}
              </APill>
              <span class="wd-info__muted">이용권 {{ planCount }}개</span>
            </template>
            <span v-else class="wd-info__muted">이용권 없음</span>
          </dd>
        </div>
        <div class="wd-info__row">
          <dt>완료 메일</dt>
          <dd>
            <template v-if="record.confirmationSentAt">발송 {{ formatDateTime(record.confirmationSentAt) }}</template>
            <span v-else class="wd-info__muted">미발송</span>
          </dd>
        </div>
        <div class="wd-info__row">
          <dt>동의 방식</dt>
          <dd>
            {{ methodText }}
            <span v-if="record.noticeVersion" class="wd-info__muted">· 안내문 v{{ record.noticeVersion }}</span>
          </dd>
        </div>
        <div class="wd-info__row">
          <dt>요청 IP</dt>
          <dd><span class="wd-info__mono">{{ record.ipAddress || '-' }}</span></dd>
        </div>
      </dl>

      <div v-if="planCount > 0" class="wd-info__plans">
        <div class="wd-info__plans-head">탈퇴 시점 보유 이용권</div>
        <ATable :columns="planColumns" :rows="plans" dense>
          <template #cell:textbook="{ row }">
            <span class="wd-info__strong">{{ row.textbookName || '-' }}</span>
          </template>
          <template #cell:type="{ row }">{{ planTypeText(row) }}</template>
          <template #cell:end="{ row }">{{ planEndText(row) }}</template>
          <template #cell:payment="{ row }">
            <span class="wd-info__mono">{{ row.paymentIdx != null ? `#${row.paymentIdx}` : '-' }}</span>
          </template>
        </ATable>
      </div>
    </div>
  </ACard>
</template>

<script>
import ACard from "@/components/Pineapple/ACard.vue";
import APill from "@/components/Pineapple/APill.vue";
import ATable from "@/components/Pineapple/ATable.vue";
import AEmpty from "@/components/Pineapple/AEmpty.vue";
import ABtn from "@/components/Pineapple/ABtn.vue";

// 백엔드 회신의 표시 제안(docs/회원관리_API.md §3.5)을 그대로 따릅니다.
const SOURCE_TEXT = { self: "본인 탈퇴", admin: "관리자 처리" };
// reauthMethod 는 현재 email_code 하나뿐(이메일·SNS 계정 공통). 모르는 값은 원문 그대로 보여줍니다.
const REAUTH_TEXT = { email_code: "이메일 인증코드 확인" };
const METHOD_TEXT = { checkbox: "체크박스" };
const GRADE_TEXT = { standard: "스탠다드", premium: "프리미엄" };

export default {
  name: "WithdrawalInfoCard",
  components: { ACard, APill, ATable, AEmpty, ABtn },
  props: {
    // GET /roles/users/{idx}/withdrawal 응답 또는 GET /roles/withdrawals 배열의 원소. 404 면 null.
    record: { type: Object, default: null },
    loading: { type: Boolean, default: false },
    // 이메일 검색 결과처럼 사용자 맥락 밖에서 쓸 때 사용자 번호와 상세 보기 버튼을 함께 보여줍니다.
    showUser: { type: Boolean, default: false },
    flat: { type: Boolean, default: false },
  },
  emits: ["view-user"],
  data() {
    return {
      planColumns: [
        { key: "textbook", label: "교재" },
        { key: "type", label: "종류", width: 150 },
        { key: "end", label: "종료일", width: 120 },
        { key: "payment", label: "결제 번호", width: 100, align: "center" },
      ],
    };
  },
  computed: {
    plans() {
      return Array.isArray(this.record?.activePlans) ? this.record.activePlans : [];
    },
    planCount() {
      return Number(this.record?.activePlanCount ?? this.plans.length) || 0;
    },
    sourceText() {
      return SOURCE_TEXT[this.record?.source] || this.record?.source || "-";
    },
    sourcePillKind() {
      return this.record?.source === "admin" ? "warn" : "info";
    },
    reauthText() {
      if (this.record?.source === "admin") return "해당 없음 (관리자 처리)";
      const m = this.record?.reauthMethod;
      return m ? REAUTH_TEXT[m] || m : "-";
    },
    methodText() {
      const m = this.record?.method;
      return m ? METHOD_TEXT[m] || m : "-";
    },
  },
  methods: {
    formatDateTime(s) {
      if (!s) return "-";
      const d = new Date(s);
      return Number.isNaN(d.getTime()) ? s : d.toLocaleString("ko-KR");
    },
    planTypeText(p) {
      if (p.planType === "book") return "도서 이용권";
      if (p.planType === "feature") {
        const grade = GRADE_TEXT[p.grade] || p.grade;
        return grade ? `기간제 ${grade}` : "기간제";
      }
      return p.planType || "-";
    },
    planEndText(p) {
      if (p.isPermanent || !p.endDate) return "영구";
      return p.endDate;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.wd-info {
  display: flex;
  flex-direction: column;
  gap: 20px;

  &__title {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 800;
    color: $p-ink;
  }

  &__title-icon {
    color: $p-point-deep;
    font-size: 16px !important;
  }

  &__title-user {
    margin-left: 4px;
    font-family: $p-font-mono;
    font-size: 12px;
    font-weight: 600;
    color: $p-ink-mid;
  }

  &__loading {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 12px;
    color: $p-ink-mid;
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px 24px;
    margin: 0;
    @media (max-width: 720px) { grid-template-columns: 1fr; }
  }

  &__row {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;

    > dt {
      font-size: 12px;
      font-weight: 700;
      color: $p-ink-mid;
    }

    > dd {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 8px;
      margin: 0;
      font-size: 13px;
      color: $p-ink;
    }
  }

  &__muted {
    font-size: 12px;
    color: $p-ink-mid;
  }

  &__mono {
    font-family: $p-font-mono;
    font-size: 12px;
  }

  &__strong {
    font-weight: 700;
  }

  &__plans {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__plans-head {
    font-size: 12px;
    font-weight: 800;
    color: $p-ink;
  }
}
</style>
